import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Heart, Loader2, Eye, EyeOff, Mail, Lock, User, Calendar, ArrowLeft } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { portalAuthApi } from '@services/portalAuth.service'
import { useToast } from '@hooks/use-toast'
import { cn } from '@lib/utils'

export function PortalRegisterPage() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    date_of_birth: '',
    mrn: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await portalAuthApi.register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || undefined,
        date_of_birth: formData.date_of_birth,
        mrn: formData.mrn || undefined,
      })

      toast({
        title: 'Registration successful!',
        description: response.message,
      })

      navigate('/portal/login')
    } catch (error) {
      toast({
        title: 'Registration failed',
        description: 'Please check your information and try again.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-medical-50 via-white to-medical-100 dark:from-slate-950 dark:via-slate-900 dark:to-medical-950 p-4">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-b from-medical-100/50 to-transparent rounded-full blur-3xl dark:from-medical-900/20" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-t from-blue-100/50 to-transparent rounded-full blur-3xl dark:from-blue-900/20" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="flex flex-col items-center mb-6">
          <div className="h-16 w-16 rounded-2xl gradient-medical flex items-center justify-center shadow-2xl shadow-medical-500/30 mb-4">
            <Heart className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100 tracking-tight">
            Crear cuenta
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Regístrate para acceder al portal del paciente
          </p>
        </div>

        <Card className="shadow-2xl border-medical-100 dark:border-medical-800">
          <CardHeader className="space-y-1 text-center pb-4">
            <CardTitle className="text-xl font-semibold">
              Registro de paciente
            </CardTitle>
            <CardDescription>
              Ingresa tus datos para crear tu cuenta del portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-sm font-medium">First Name</Label>
                  <div className="relative">
                    <User
                      className={cn(
                        "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors",
                        focusedField === 'first_name' ? "text-medical-600" : "text-muted-foreground"
                      )}
                    />
                    <Input
                      id="first_name"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      onFocus={() => setFocusedField('first_name')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 h-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-sm font-medium">Last Name</Label>
                  <div className="relative">
                    <User
                      className={cn(
                        "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors",
                        focusedField === 'last_name' ? "text-medical-600" : "text-muted-foreground"
                      )}
                    />
                    <Input
                      id="last_name"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      onFocus={() => setFocusedField('last_name')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 h-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <div className="relative">
                  <Mail
                    className={cn(
                      "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors",
                      focusedField === 'email' ? "text-medical-600" : "text-muted-foreground"
                    )}
                  />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    className="pl-10 h-10"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock
                    className={cn(
                      "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors",
                      focusedField === 'password' ? "text-medical-600" : "text-muted-foreground"
                    )}
                  />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="pl-10 pr-10 h-10"
                    required
                    minLength={12}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Must be at least 12 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_of_birth" className="text-sm font-medium">Date of Birth</Label>
                <div className="relative">
                  <Calendar
                    className={cn(
                      "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors",
                      focusedField === 'dob' ? "text-medical-600" : "text-muted-foreground"
                    )}
                  />
                  <Input
                    id="date_of_birth"
                    type="date"
                    lang="es-MX"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    onFocus={() => setFocusedField('dob')}
                    onBlur={() => setFocusedField(null)}
                    className="pl-10 h-10"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">El formato de fecha es día/mes/año</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mrn" className="text-sm font-medium">
                  Número de Expediente - Opcional
                </Label>
                <Input
                  id="mrn"
                  placeholder="Si ya tienes un número de expediente, ingrésalo aquí"
                  value={formData.mrn}
                  onChange={(e) => setFormData({ ...formData, mrn: e.target.value })}
                  className="h-10"
                />
                <p className="text-xs text-muted-foreground">
                  Vincúlalo a un expediente existente si ya cuentas con uno
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-11 gradient-medical hover:opacity-90 transition-all shadow-lg shadow-medical-500/25 text-base font-medium"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creando cuenta...
                  </>
                ) : (
                  'Crear cuenta'
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-border">
              <Link
                to="/portal/login"
                className="text-sm text-medical-600 hover:text-medical-700 font-medium inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver al inicio de sesión
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
